/* ==========================================================================
   AZ MOTORSPORT — EDITORIAL / BLOG BEHAVIOUR
   ==========================================================================
   Two custom elements, no dependencies, no third-party origins, no analytics
   (so no Loi 25 consent gating required).

   <azm-article-toc>      anchor scrolling + section spy (active / passed)
   <azm-reading-progress> scaleX progress bar for the article body

   Both degrade to plain server-rendered HTML if JS never runs: the TOC is a
   real anchor list, the progress bar stays hidden.
   ========================================================================== */

(() => {
  const REDUCED_MOTION = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const headerOffset = () => {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(
      '--header-height'
    );
    return parseFloat(raw) || 0;
  };

  /* ---------------------------------------------------------------------- */
  /* Table of contents                                                      */
  /* ---------------------------------------------------------------------- */
  if (!customElements.get('azm-article-toc')) {
    class AzmArticleToc extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('.azm-toc__item'));
        this.links = Array.from(this.querySelectorAll('.azm-toc__link'));
        this.disclosure = this.querySelector('details');

        this.targets = this.links
          .map((link) => {
            const id = (link.getAttribute('href') || '').slice(1);
            return id ? document.getElementById(id) : null;
          })
          .filter(Boolean);

        if (!this.targets.length) return;

        this.layout = this.dataset.layout || 'rail';
        this.list = this.querySelector('.azm-secnav__list');

        this.onLinkClick = this.onLinkClick.bind(this);
        this.links.forEach((link) =>
          link.addEventListener('click', this.onLinkClick)
        );

        this.observe();
        this.followHeader();
        this.watchReveal();
      }

      /* In 'map' mode the bar stays out of the way until the section map at the
         top of the article has scrolled off screen, so the reader never sees two
         navigators at once. */
      watchReveal() {
        if (this.layout !== 'bar') return;
        const mapId = this.dataset.revealAfter;
        if (!mapId) {
          this.classList.remove('is-hidden');
          return;
        }
        const map = document.getElementById(mapId);
        if (!map) {
          this.classList.remove('is-hidden');
          return;
        }

        this.revealObserver = new IntersectionObserver(
          ([entry]) => {
            // reveal once the map is gone above the viewport, not when the
            // reader scrolls back up past the article entirely
            const scrolledPast =
              !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.classList.toggle('is-hidden', !scrolledPast);
          },
          { threshold: 0 }
        );
        this.revealObserver.observe(map);
      }

      /* The site header hides on scroll-down and slides back in on scroll-up
         (.section-header toggles top: 0 / -header-height). A static offset would
         therefore either sit under the header or leave a gap, so track its real
         on-screen bottom edge and pin the bar directly beneath it. */
      followHeader() {
        if (this.layout !== 'bar') return;
        this.siteHeader = document.querySelector('.section-header');
        if (!this.siteHeader) return;

        this.headerTicking = false;
        this.syncTop = this.syncTop.bind(this);
        this.onHeaderScroll = () => {
          if (this.headerTicking) return;
          this.headerTicking = true;
          window.requestAnimationFrame(() => {
            this.syncTop();
            this.headerTicking = false;
          });
        };

        window.addEventListener('scroll', this.onHeaderScroll, { passive: true });
        window.addEventListener('resize', this.onHeaderScroll, { passive: true });
        this.syncTop();
      }

      syncTop() {
        if (!this.siteHeader) return;
        const bottom = this.siteHeader.getBoundingClientRect().bottom;
        this.style.setProperty(
          '--azm-secnav-top',
          Math.max(0, Math.round(bottom)) + 'px'
        );
      }


      /* Keep the active chip visible without scrolling the page itself —
         scrollIntoView would do both, so the container is scrolled directly. */
      keepChipVisible(index) {
        if (this.layout !== 'bar' || !this.list || index < 0) return;
        const item = this.items[index];
        if (!item) return;

        const pad = 16;
        const itemLeft = item.offsetLeft;
        const itemRight = itemLeft + item.offsetWidth;
        const viewLeft = this.list.scrollLeft;
        const viewRight = viewLeft + this.list.clientWidth;
        let target = null;

        if (itemLeft < viewLeft + pad) target = itemLeft - pad;
        else if (itemRight > viewRight - pad)
          target = itemRight - this.list.clientWidth + pad;

        if (target === null) return;
        this.list.scrollTo({
          left: Math.max(0, target),
          behavior: REDUCED_MOTION() ? 'auto' : 'smooth'
        });
      }

      disconnectedCallback() {
        this.links.forEach((link) =>
          link.removeEventListener('click', this.onLinkClick)
        );
        if (this.observer) this.observer.disconnect();
        if (this.revealObserver) this.revealObserver.disconnect();
        if (this.onHeaderScroll) {
          window.removeEventListener('scroll', this.onHeaderScroll);
          window.removeEventListener('resize', this.onHeaderScroll);
        }
      }

      onLinkClick(event) {
        const id = (event.currentTarget.getAttribute('href') || '').slice(1);
        const target = document.getElementById(id);
        if (!target) return; // let the browser handle a broken anchor

        event.preventDefault();
        const top =
          target.getBoundingClientRect().top +
          window.scrollY -
          headerOffset() -
          24;

        window.scrollTo({
          top,
          behavior: REDUCED_MOTION() ? 'auto' : 'smooth'
        });

        // keep the URL clean but make the section focusable for screen readers
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });

        // collapse the mobile disclosure after a jump
        if (this.disclosure && this.disclosure.open && window.innerWidth < 990) {
          this.disclosure.open = false;
        }
      }

      observe() {
        // A heading is "current" once it crosses the header line; the last one
        // crossed wins. rootMargin pins the trigger line just below the header.
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const index = this.targets.indexOf(entry.target);
              if (index === -1) return;
              this.state = this.state || [];
              this.state[index] = entry.boundingClientRect.top < entry.rootBounds.top;
            });
            this.paint();
          },
          {
            rootMargin: `-${Math.round(headerOffset()) + 24}px 0px -70% 0px`,
            threshold: 0
          }
        );

        this.targets.forEach((target) => this.observer.observe(target));
      }

      paint() {
        if (!this.state) return;
        // highest index whose heading is above the trigger line
        let active = -1;
        for (let i = 0; i < this.targets.length; i += 1) {
          if (this.state[i]) active = i;
        }

        this.items.forEach((item, i) => {
          item.classList.toggle('is-active', i === active);
          item.classList.toggle('is-past', i < active);
          const link = this.links[i];
          if (link) {
            if (i === active) {
              link.setAttribute('aria-current', 'true');
            } else {
              link.removeAttribute('aria-current');
            }
          }
        });

        if (active !== this.lastActive) {
          this.lastActive = active;
          this.keepChipVisible(active);
        }
      }
    }

    customElements.define('azm-article-toc', AzmArticleToc);
  }

  /* ---------------------------------------------------------------------- */
  /* Reading progress                                                       */
  /* ---------------------------------------------------------------------- */
  if (!customElements.get('azm-reading-progress')) {
    class AzmReadingProgress extends HTMLElement {
      connectedCallback() {
        this.bar = this.querySelector('.azm-progress__bar');
        this.trackedId = this.getAttribute('data-track');
        this.tracked = this.trackedId
          ? document.getElementById(this.trackedId)
          : null;

        if (!this.bar || !this.tracked) return;

        this.siteHeader = document.querySelector('.section-header');
        this.ticking = false;
        this.onScroll = this.onScroll.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.update();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        window.requestAnimationFrame(() => {
          this.update();
          this.ticking = false;
        });
      }

      /* Stack under whatever chrome is on screen: the section bar when it is
         showing, otherwise the site header. Both move as you scroll, so this is
         recomputed per frame rather than assuming --header-height. */
      syncTop() {
        const bar = document.querySelector(
          '.azm-secnav:not(.is-hidden):not(.azm-secnav--static)'
        );
        const anchor = bar || this.siteHeader;
        if (!anchor) return;
        const bottom = anchor.getBoundingClientRect().bottom;
        this.style.setProperty(
          '--azm-progress-top',
          Math.max(0, Math.round(bottom)) + 'px'
        );
      }

      update() {
        this.syncTop();

        const rect = this.tracked.getBoundingClientRect();
        const viewport = window.innerHeight;
        const scrollable = rect.height - viewport;

        // Visible while any part of the article body is still being read.
        const started = rect.top < viewport * 0.85;
        const finished = rect.bottom < viewport * 0.1;
        this.classList.toggle('is-active', started && !finished);

        let progress;
        if (scrollable > 0) {
          progress = -rect.top / scrollable;
        } else {
          // body shorter than the viewport: progress by how far its end has risen
          const span = viewport - rect.height;
          progress = span > 0 ? (viewport - rect.bottom) / span : 1;
        }

        progress = Math.min(Math.max(progress, 0), 1);
        this.bar.style.transform = `scaleX(${progress.toFixed(4)})`;
      }
    }

    customElements.define('azm-reading-progress', AzmReadingProgress);
  }
})();
